<template>
  <div>
    <headerSection></headerSection>
    <div class="continue-page">
      <!-- Hero Section -->
      <div class="continue-hero">
        <div class="hero-bg-effects">
          <div class="floating-ball ball-1"></div>
          <div class="floating-ball ball-2"></div>
          <div class="floating-ball ball-3"></div>
        </div>
        <div class="container">
          <div class="hero-content">
            <div class="hero-icon">🔥</div>
            <h1 class="hero-title">
              <span class="title-glow">{{ $t('continue_page.title') }}</span>
            </h1>
            <p class="hero-description">
              {{ $t('continue_page.hero_description') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="container continue-content">
        <!-- Stats Section -->
        <div class="stats-section">
          <div class="stats-grid">
            <!-- Promotion Name -->
            <div class="stat-card">
              <div class="stat-icon-wrapper fire">
                <span class="stat-icon">🎁</span>
              </div>
              <div class="stat-info">
                <span class="stat-label">โปรโมชั่น</span>
                <span class="stat-value">{{ (continues && continues.promotion) ? continues.promotion.name : $t('continue_page.title2') }}</span>
              </div>
            </div>

            <!-- Total Bonus -->
            <div class="stat-card">
              <div class="stat-icon-wrapper gold">
                <span class="stat-icon">💰</span>
              </div>
              <div class="stat-info">
                <span class="stat-label">{{ $t('continue_page.title3') }}</span>
                <span class="stat-value gold">{{ numberWithCommas(continues.total) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Streak Progress Section -->
        <div class="streak-section">
          <div class="streak-card">
            <div class="streak-header">
              <span class="streak-icon">📅</span>
              <span class="streak-title">{{ $t('continue_page.streak_title') }}</span>
            </div>

            <div class="streak-progress">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: `${(dots.length / 7) * 100}%` }"
                ></div>
              </div>
              <div class="progress-text">{{ dots.length }}{{ $t('continue_page.day_suffix') }}</div>
            </div>

            <div class="days-grid">
              <div
                v-for="day in 7"
                :key="day"
                class="day-item"
                :class="{
                  'active': dots.length >= day,
                  'current': dots.length === day - 1
                }"
              >
                <div class="day-circle">
                  <span v-if="dots.length >= day" class="day-check">✓</span>
                  <span v-else class="day-number">{{ day }}</span>
                </div>
                <div class="day-label">{{ $t('continue_page.day_label') }} {{ day }}</div>
              </div>
            </div>

            <div class="streak-status">
              <div v-if="dots.length === 7" class="status-success">
                <span class="status-icon">🎉</span>
                <span class="status-text">{{ $t('continue_page.status_complete') }}</span>
              </div>
              <div v-else-if="dots.length > 0" class="status-progress">
                <span class="status-icon">🔥</span>
                <span class="status-text">{{ $t('continue_page.status_active') }} {{ dots.length }} {{ $t('continue_page.day_label') }}</span>
              </div>
              <div v-else class="status-pending">
                <span class="status-icon">💡</span>
                <span class="status-text">{{ $t('continue_page.status_inactive') }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Transfer Button -->
        <div class="transfer-section">
          <button
            class="transfer-btn"
            :class="{ 'disabled': !continues || !continues.total || continues.total <= 0 }"
            @click="moveContinuesToWallet()"
            :disabled="!continues || !continues.total || continues.total <= 0"
          >
            <span class="transfer-icon">💳</span>
            <span class="transfer-text">{{ $t('partner_title7') }}</span>
            <span class="btn-shine"></span>
          </button>
        </div>

        <!-- Rules Section -->
        <div class="rules-section">
          <div class="rules-card">
            <div class="rules-header">
              <span class="rules-icon">📋</span>
              <span class="rules-title">{{ $t('continue_page.rules_title') }}</span>
            </div>
            <div class="rules-content">
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.member_7_days') }}</span>
              </div>
              <div class="rule-item highlight">
                <span class="rule-bullet">✨</span>
                <span class="rule-text">{{ $t('continue_page.rules.deposit_1000_bonus') }}</span>
              </div>
              <div class="rule-item highlight">
                <span class="rule-bullet">✨</span>
                <span class="rule-text">{{ $t('continue_page.rules.deposit_2000_bonus') }}</span>
              </div>
              <div class="rule-item highlight">
                <span class="rule-bullet">✨</span>
                <span class="rule-text">{{ $t('continue_page.rules.deposit_3000_bonus') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.no_bonus_deposit') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.casino_games_only') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.no_feature_buy') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.no_exceed_bonus') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.withdraw_3x') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.max_withdraw_3x') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.bonus_credit_only') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.credit_below_10') }}</span>
              </div>
              <div class="rule-item">
                <span class="rule-bullet">•</span>
                <span class="rule-text">{{ $t('continue_page.rules.auto_claim_time') }}</span>
              </div>
              <div class="rule-item warning">
                <span class="rule-bullet">⚠</span>
                <span class="rule-text">{{ $t('continue_page.rules.violation_warning') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <footerSection></footerSection>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/database'
import moment from 'moment-timezone'
import router from './../router'
const axios = require('axios').default
export default {
  name: 'continues',
  components: {
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue')
  },
  data: function () {
    return {
      continues: [],
      dots: [],
      disabled: false
    }
  },
  computed: {
    user () {
      return this.$store.state.user.user
    }
  },
  methods: {
    scrollToTop () {
      window.scrollTo(0, 0)
    },
    moveContinuesToWallet () {
      if (this.continues.total && this.continues.key && !this.disabled) {
        this.disabled = true;
        this.$store.commit('user/SET_LOADING', true)
        this.$store.dispatch('user/moveCreditToWallet').finally((res) => {
          setTimeout(async () => {
            const eligibility = await this.$store.dispatch('user/checkBonusClaimEligibility', { type: 'continuous' })
            if (eligibility.blocked) {
              this.showBonusClaimBlockedAlert(eligibility)
              this.$store.commit('user/SET_LOADING', false)
              return
            }

            let check = false
            await firebase.firestore().collection('history').where('userkey', '==', this.user.key).where('status', 'not-in', ['ดำเนินการแล้ว', 'ยกเลิก']).where('type', '==', 'ถอน').limit(1).get().then((result) => {
              if (result.size) {
                check = true
              }
            })
            if (!check) {
              axios.get(`https://api.funbeyond.cc/qcontinuous/continuous/${this.continues.key}`)
              setTimeout(() => {
                this.getContinues()
                this.$store.commit('user/SET_LOADING', false)
              }, 2000)
            } else {
              this.$swal({ icon: 'error', title: this.$t('partner_page.alert_title'), text: this.$t('continue_page.alert_wait_withdraw') })
              this.$store.commit('user/SET_LOADING', false)
            }
          }, 2000)
        }).catch(err => { console.error(err) })
        setTimeout(() => {
          this.disabled = false;
        }, 3000);
      }
    },
    showBonusClaimBlockedAlert (eligibility) {
      const total = Number(eligibility.promotionBalanceTotal || eligibility.totalBalance || 0).toFixed(2)
      const limit = Number(eligibility.limit || 10).toFixed(2)
      const lineLink = this.$store.getters['settings/adminContactLineLink'] || this.$store.getters['settings/linkLine'] || 'https://lin.ee/xalM4XE'
      return this.$swal({
        icon: 'error',
        title: 'เงื่อนไข',
        html: `ต้องมียอดคงเหลือรวมต่ำกว่า ${limit} บาท ถึงรับได้ค่ะ (ยอดรวมปัจจุบัน ${total} บาท)<br><br>สอบถามข้อมูลเครดิตเพิ่มเติม กรุณาติดต่อแอดมิน`,
        showCancelButton: true,
        confirmButtonText: 'ตกลง',
        cancelButtonText: 'ติดต่อแอดมิน',
        reverseButtons: false
      }).then((swalResult) => {
        if (swalResult && swalResult.dismiss === 'cancel' && lineLink) {
          window.open(lineLink, '_blank')
        }
        return swalResult
      })
    },
    numberWithCommas (x) {
      if (!x) x = 0
      return x.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    getUserId () {
      const user = localStorage.getItem('userData')
      if (user) {
        const _user = JSON.parse(user)
        return _user
      } else {
        firebase.auth().signOut()
        localStorage.removeItem('userData')
        router.replace('/login')
      }
    },
    async getContinues () {
      const user = this.getUserId()
      if (user) {
        await firebase.firestore().collection('continuous_deposit').where('date', '==', moment().tz('Asia/Bangkok').add(-1, 'days').format('YYYY-MM-DD')).where('userkey', '==', user.key).where('status', '==', false).get().then((snapshot) => {
          if (!snapshot.size) {
            this.continues = []
            this.dots = []
          }
          snapshot.forEach((doc) => {
            const data = doc.data()
            data.key = doc.id
            this.continues = data
          })
        })
        const fixed = 1000
        let last = {}
        await firebase.firestore().collection('continuous_deposit_all').where('userkey', '==', user.key).where('status', '==', true).orderBy('date', 'desc').limit(1).get().then((snapshot) => {
          snapshot.forEach((doc) => {
            const data = doc.data()
            data.key = doc.id
            last = data
          })
        })
        const dateCheck = []
        if (last && last.date) {
          await firebase.firestore().collection('daily_deposit').where('createdate', '>', last.date).where('createdate', '<=', moment().tz('Asia/Bangkok').add(-1, 'days').format('YYYY-MM-DD')).where('userkey', '==', user.key).orderBy('createdate', 'desc').limit(7).get().then((snapshot) => {
            snapshot.forEach((doc) => {
              if (doc.data().total >= fixed) {
                dateCheck[doc.data().createdate] = true
              }
            })
          })
        } else {
          await firebase.firestore().collection('daily_deposit').where('createdate', '<=', moment().tz('Asia/Bangkok').add(-1, 'days').format('YYYY-MM-DD')).where('userkey', '==', user.key).orderBy('createdate', 'desc').limit(7).get().then((snapshot) => {
            snapshot.forEach((doc) => {
              if (doc.data().total >= fixed) {
                dateCheck[doc.data().createdate] = true
              }
            })
          })
        }
        if (dateCheck) {
          for (let i = 1; i <= 7; i++) {
            if (dateCheck[moment().tz('Asia/Bangkok').add(-i, 'days').format('YYYY-MM-DD')]) {
              this.dots.push(true)
            } else {
              break
            }
          }
        }
      }
    }
  },
  mounted () {
    this.scrollToTop()
    this.getContinues()
  }
}
</script>

<style scoped>
/* ============================================
   CONTINUE PAGE - MODERN DESIGN
   ============================================ */

.continue-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1b1b41 0%, #492666 50%, #2b2727 100%);
  padding-bottom: 100px;
}

/* ============================================
   HERO SECTION
   ============================================ */

.continue-hero {
  position: relative;
  padding: 40px 0 30px;
  overflow: hidden;
  background: linear-gradient(180deg,
    rgba(131, 9, 162, 0.1) 0%,
    transparent 100%
  );
}

.hero-bg-effects {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-ball {
  position: absolute;
  border-radius: 50%;
  opacity: 0.15;
}

.ball-1 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #8309a2, #ffc107);
  top: -50px;
  left: -50px;
  animation: float1 6s ease-in-out infinite;
}

.ball-2 {
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, #fe8a02, #ffc107);
  top: 50%;
  right: -30px;
  animation: float2 8s ease-in-out infinite;
}

.ball-3 {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #492666, #8309a2);
  bottom: -20px;
  left: 30%;
  animation: float3 7s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(30px, 20px) rotate(180deg); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-20px, -30px) scale(1.1); }
}

@keyframes float3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(20px, -20px); }
}

.hero-content {
  text-align: center;
  position: relative;
  z-index: 1;
}

.hero-icon {
  font-size: 60px;
  margin-bottom: 15px;
  animation: flame 1.5s ease-in-out infinite;
}

@keyframes flame {
  0%, 100% { transform: scale(1) translateY(0); }
  50% { transform: scale(1.1) translateY(-5px); filter: brightness(1.3); }
}

.hero-title {
  margin: 0 0 15px;
}

.title-glow {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #ce93d8, #8309a2, #ce93d8);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: purpleShine 3s linear infinite;
}

@keyframes purpleShine {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}

.hero-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

/* ============================================
   CONTINUE CONTENT
   ============================================ */

.continue-content {
  padding: 20px 15px;
}

/* ============================================
   STATS SECTION
   ============================================ */

.stats-section {
  margin-bottom: 30px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.stat-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}

.stat-icon-wrapper {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.stat-icon-wrapper.fire {
  background: linear-gradient(135deg, rgba(255, 87, 34, 0.2), rgba(255, 152, 0, 0.1));
}

.stat-icon-wrapper.gold {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.2), rgba(255, 152, 0, 0.1));
}

.stat-icon {
  font-size: 24px;
}

.stat-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}

.stat-value.gold {
  color: #ffc107;
  text-shadow: 0 0 15px rgba(255, 193, 7, 0.5);
}

/* ============================================
   STREAK SECTION
   ============================================ */

.streak-section {
  margin-bottom: 30px;
}

.streak-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(131, 9, 162, 0.3);
  border-radius: 20px;
  padding: 25px;
}

.streak-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.streak-icon {
  font-size: 28px;
}

.streak-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.streak-progress {
  margin-bottom: 25px;
}

.progress-bar {
  height: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #8309a2, #ffc107);
  border-radius: 10px;
  transition: width 0.5s ease;
  box-shadow: 0 0 15px rgba(255, 193, 7, 0.5);
}

.progress-text {
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #ffc107;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}

.day-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.day-circle {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.day-item.active .day-circle {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  border-color: #4caf50;
  box-shadow: 0 0 15px rgba(76, 175, 80, 0.5);
}

.day-item.current .day-circle {
  background: linear-gradient(135deg, #8309a2, #ffc107);
  border-color: #ffc107;
  box-shadow: 0 0 15px rgba(255, 193, 7, 0.5);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.day-check {
  font-size: 20px;
  color: #fff;
}

.day-number {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
}

.day-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
}

.streak-status {
  padding: 15px;
  border-radius: 12px;
  text-align: center;
}

.status-success {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  padding: 12px;
  border-radius: 12px;
}

.status-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.3);
  padding: 12px;
  border-radius: 12px;
}

.status-pending {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px;
  border-radius: 12px;
}

.status-icon {
  font-size: 20px;
}

.status-text {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

/* ============================================
   TRANSFER SECTION
   ============================================ */

.transfer-section {
  margin-bottom: 30px;
}

.transfer-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 18px 32px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #8309a2 0%, #492666 100%);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  overflow: hidden;
  box-shadow:
    0 4px 15px rgba(131, 9, 162, 0.4),
    0 0 30px rgba(131, 9, 162, 0.2);
  transition: all 0.3s ease;
}

.transfer-btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow:
    0 6px 20px rgba(131, 9, 162, 0.5),
    0 0 40px rgba(131, 9, 162, 0.3);
}

.transfer-btn.disabled {
  background: linear-gradient(135deg, #555, #444);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
  box-shadow: none;
}

.transfer-icon {
  font-size: 24px;
}

.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shine 3s ease-in-out infinite;
}

.transfer-btn.disabled .btn-shine {
  display: none;
}

@keyframes shine {
  0% { left: -100%; }
  50%, 100% { left: 100%; }
}

/* ============================================
   RULES SECTION
   ============================================ */

.rules-section {
  margin-bottom: 30px;
}

.rules-card {
  background: linear-gradient(135deg,
    rgba(131, 9, 162, 0.1) 0%,
    rgba(73, 38, 102, 0.05) 100%
  );
  border: 1px solid rgba(131, 9, 162, 0.3);
  border-radius: 20px;
  padding: 25px;
}

.rules-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.rules-icon {
  font-size: 28px;
}

.rules-title {
  font-size: 16px;
  font-weight: 600;
  color: #ce93d8;
}

.rules-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rule-item {
  display: flex;
  gap: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
}

.rule-item.highlight {
  color: #ffc107;
}

.rule-item.warning {
  color: #ff9800;
}

.rule-bullet {
  flex-shrink: 0;
  font-size: 16px;
}

.rule-text {
  flex: 1;
}

/* ============================================
   RESPONSIVE
   ============================================ */

@media (max-width: 480px) {
  .hero-icon {
    font-size: 48px;
  }

  .title-glow {
    font-size: 24px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-value {
    font-size: 16px;
  }

  .days-grid {
    gap: 6px;
  }

  .day-circle {
    width: 32px;
    height: 32px;
  }

  .day-check {
    font-size: 16px;
  }

  .day-number {
    font-size: 12px;
  }

  .day-label {
    font-size: 9px;
  }

  .rule-item {
    font-size: 13px;
  }
}

@media (min-width: 769px) {
  .continue-content {
    max-width: 600px;
    margin: 0 auto;
  }

  .title-glow {
    font-size: 32px;
  }
}
</style>
